import sanitizeHtml from 'sanitize-html';
import { ensureUrlHasScheme } from './ensure-url-has-scheme';

const FURTHER_BLANK_ATTR = 'data-further-blank';

type MidParagraphBreakSegment = {
  content: string;
  blankParagraphCountAfter: number;
};

// Matches a single paragraph and captures:
// - attrs: any attributes on the <p> tag
// - body: the inner HTML of the paragraph
const PARAGRAPH_CAPTURE_REGEX = /<p([^>]*)>([\s\S]*?)<\/p>/gi;
const LEADING_BREAK_SEQUENCE_REGEX = /^((?:\s*<br\s*\/?>\s*)+)([\s\S]*)$/i;
const MID_PARAGRAPH_BREAK_SEQUENCE_REGEX = /((?:\s*<br\s*\/?>\s*){2,})/gi;

// Matches any "blank" paragraph content we want to treat as whitespace, including NBSP variants.
const WHITESPACE_OR_NBSP_REGEX = /(\s|&nbsp;|&#160;|&#xA0;|&#x00A0;|\u00A0)+/gi;

type Options = {
  openExternalLinksInNewTab?: boolean;
  allowImages?: boolean;
};

const defaultOptions: Options = {
  openExternalLinksInNewTab: false,
  allowImages: true,
};

const countBreakTags = (content: string) => {
  const breakTagRegex = /<br\s*\/?>/gi;
  let count = 0;

  while (breakTagRegex.exec(content)) {
    count += 1;
  }

  return count;
};

const getLeadingBreakMatch = (content: string) =>
  LEADING_BREAK_SEQUENCE_REGEX.exec(content);

const getMidParagraphBreakSegments = (content: string) => {
  const breakSequenceRegex = new RegExp(MID_PARAGRAPH_BREAK_SEQUENCE_REGEX);
  const segments: Array<MidParagraphBreakSegment> = [];
  let lastIndex = 0;
  let match = breakSequenceRegex.exec(content);

  while (match) {
    segments.push({
      content: content.slice(lastIndex, match.index),
      blankParagraphCountAfter: Math.max(countBreakTags(match[1]) - 1, 0),
    });
    lastIndex = match.index + match[1].length;
    match = breakSequenceRegex.exec(content);
  }

  if (!segments.length) {
    return segments;
  }

  segments.push({
    content: content.slice(lastIndex),
    blankParagraphCountAfter: 0,
  });

  return segments;
};

const sanitiseHtml = (
  dirtyHtml?: string | null,
  options: Options = defaultOptions,
) => {
  if (!dirtyHtml) {
    return undefined;
  }

  // Mark NBSP-only paragraphs before normalising NBSPs to regular spaces.
  // This lets downstream PDF rendering treat these as intentional blank lines.
  let cleanedHtml = dirtyHtml.replace(
    PARAGRAPH_CAPTURE_REGEX,
    (match: string, attrs: string, body: string) => {
      const leadingBreakMatch = getLeadingBreakMatch(body);
      const leadingBreakCount = leadingBreakMatch
        ? countBreakTags(leadingBreakMatch[1])
        : 0;
      const paragraphBody = leadingBreakMatch?.[2] ?? body;
      const blankParagraphs = '<p data-further-blank="1"></p>'.repeat(
        leadingBreakCount,
      );
      const hasNbsp =
        paragraphBody.includes('\u00A0') ||
        paragraphBody.includes('&nbsp;') ||
        paragraphBody.includes('&#160;') ||
        paragraphBody.includes('&#xA0;') ||
        paragraphBody.includes('&#x00A0;');
      const isOnlyWhitespaceOrNbsp = !paragraphBody.replace(
        WHITESPACE_OR_NBSP_REGEX,
        '',
      ).length;

      if (isOnlyWhitespaceOrNbsp) {
        if (hasNbsp && !attrs.includes(FURTHER_BLANK_ATTR)) {
          return `${blankParagraphs}<p${attrs} ${FURTHER_BLANK_ATTR}="1"></p>`;
        }

        if (!blankParagraphs) {
          // Preserve ordinary whitespace-only paragraphs unless we explicitly
          // converted leading breaks into blank paragraph markers above.
          return match;
        }

        return blankParagraphs;
      }

      const paragraphSegments = getMidParagraphBreakSegments(paragraphBody);

      if (paragraphSegments.length) {
        const rebuiltParagraphs = paragraphSegments
          .map((segment: MidParagraphBreakSegment) => {
            const normalisedSegment = segment.content.trim();
            const paragraphHtml = normalisedSegment
              ? `<p${attrs}>${normalisedSegment}</p>`
              : '';
            const blankParagraphsAfter =
              '<p data-further-blank="1"></p>'.repeat(
                segment.blankParagraphCountAfter,
              );

            return `${paragraphHtml}${blankParagraphsAfter}`;
          })
          .join('');

        return `${blankParagraphs}${rebuiltParagraphs}`;
      }

      if (!leadingBreakCount) {
        return match;
      }

      return `${blankParagraphs}<p${attrs}>${paragraphBody}</p>`;
    },
  );

  cleanedHtml = cleanedHtml
    .replace(/\u00A0/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&#160;/gi, ' ')
    .replace(/&#xA0;/gi, ' ')
    .replace(/&#x00A0;/gi, ' ');

  return sanitizeHtml(cleanedHtml, {
    allowedTags: [
      'article',
      'aside',
      'footer',
      'header',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'main',
      'nav',
      'section',
      'blockquote',
      'div',
      'hr',
      'li',
      'main',
      'ol',
      'p',
      'pre',
      'ul',
      'a',
      'b',
      'br',
      'code',
      'em',
      'i',
      'u',
      'small',
      'span',
      'strong',
      'caption',
      'col',
      'colgroup',
      'table',
      'tbody',
      'td',
      'tfoot',
      'th',
      'thead',
      'tr',
      ...(options.allowImages ? ['img'] : []),
    ],
    allowedAttributes: {
      '*': ['style'],
      img: ['src', 'alt', 'width', 'height'],
      a: ['href', 'name', 'target', 'rel'],
      p: [FURTHER_BLANK_ATTR],
    },
    allowedSchemes: ['http', 'https', 'mailto', 'tel', 'data'],
    allowedSchemesAppliedToAttributes: ['href', 'src'],
    disallowedTagsMode: 'discard',
    selfClosing: ['img', 'br', 'hr'],
    transformTags: {
      a: (_, attribs) => {
        return {
          tagName: 'a',
          attribs: {
            ...attribs,
            ...(options.openExternalLinksInNewTab && {
              target: '_blank',
              rel: 'noopener noreferrer',
            }),
            href: ensureUrlHasScheme(attribs['href']),
          },
        };
      },
    },
  });
};

export default sanitiseHtml;
